/**
 * SELECTION STORE
 * 
 * Manages UI selection state for the Factory World.
 * Handles what's currently selected, hovered, or focused.
 * 
 * This store is purely frontend state - no API integration needed.
 */

import { create } from "zustand";

type SelectionType = "project" | "agent" | "task" | "zone" | null;

interface SelectionState {
  selectedType: SelectionType;
  selectedId: number | string | null;
  hoveredProjectId: number | null;
  hoveredAgentId: string | null;
  isPanelOpen: boolean;
  activeView: "world" | "kanban" | "terminal";
  activeProjectForKanban: number | null;
  
  selectProject: (id: number | null) => void;
  selectAgent: (id: string | null) => void;
  selectTask: (id: string | null) => void;
  selectZone: (id: number | null) => void;
  clearSelection: () => void;
  setHoveredProject: (id: number | null) => void;
  setHoveredAgent: (id: string | null) => void;
  openPanel: () => void;
  closePanel: () => void;
  setActiveView: (view: "world" | "kanban" | "terminal") => void;
  openKanbanForProject: (projectId: number) => void;
}

export const useSelectionStore = create<SelectionState>()((set) => ({
  selectedType: null,
  selectedId: null,
  hoveredProjectId: null,
  hoveredAgentId: null,
  isPanelOpen: false,
  activeView: "world",
  activeProjectForKanban: null,

  selectProject: (id) =>
    set({
      selectedType: id ? "project" : null,
      selectedId: id,
      isPanelOpen: !!id,
    }),

  selectAgent: (id) =>
    set({
      selectedType: id ? "agent" : null,
      selectedId: id,
      isPanelOpen: !!id,
    }),

  selectTask: (id) =>
    set({
      selectedType: id ? "task" : null,
      selectedId: id,
      isPanelOpen: !!id,
    }),

  selectZone: (id) =>
    set({
      selectedType: id ? "zone" : null,
      selectedId: id,
      isPanelOpen: !!id,
    }),

  clearSelection: () =>
    set({
      selectedType: null,
      selectedId: null,
      isPanelOpen: false,
    }),

  setHoveredProject: (id) => set({ hoveredProjectId: id }),
  setHoveredAgent: (id) => set({ hoveredAgentId: id }),

  openPanel: () => set({ isPanelOpen: true }),
  closePanel: () => set({ isPanelOpen: false }),

  setActiveView: (view) => set({ activeView: view }),

  openKanbanForProject: (projectId) =>
    set({
      activeView: "kanban",
      activeProjectForKanban: projectId,
    }),
}));
